/**Dependecies */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  getHotels,
  getHotelAvailability,
  getHotelFilters,
} from "../services/searchService";
import { getMarkup } from "../services/adminProductService";
import { Currency, fetchMarkup, totalPrices } from "../helpers";

/**Components */
import { Row, Col, Accordion, Form } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import { SearchEngine } from "../components/SearchEngine";
import { Loader } from "../components/Loader";
import Skeleton from "react-loading-skeleton";

/**Styles */
import "../styles/hotelList.scss";

/**Images */
import { IoStarSharp } from "react-icons/io5";
import star from "../images/icons/estrella.png";

export const Alojamientos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  let requestData = [];
  const [hotels, setHotels] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [loading, setLoading] = useState(true); // Indicador de carga
  const [visibleCount, setVisibleCount] = useState(10); // Contador de resultados visibles
  const [filters, setFilters] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [totalNights, setTotalNights] = useState(0);
  const [totalRooms, setTotalRooms] = useState([]);
  const [nameDestination, setNameDestination] = useState("");
  const [selectedValues, setSelectedValues] = useState({});
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [markup, setMarkup] = useState("");
  const [paramsUrl, setParamsUrl] = useState({});
  const [totalPax, setTotalPax] = useState(0);

  // Función para parsear los parámetros de la URL
  const parseQueryParams = (query) => {
    return Object.fromEntries(new URLSearchParams(query));
  };
  const queryParams = parseQueryParams(location.search); // Extraemos los parámetros de la URL

  const construirRequest = () => {
    const checkin = queryParams.in; // Fecha de check-in
    const checkout = queryParams.out; // Fecha de check-out
    const id = queryParams.id; // ID del destino
    const tipo = queryParams.t || "destino"; // Tipo de alojamiento
    setSelectedDates([checkin, checkout]);
    // Crear habitaciones a partir de los parámetros pax
    const habitaciones = Object.keys(queryParams)
      .filter((key) => key.startsWith("pax")) // Filtramos las claves que empiezan con 'pax'
      .map((key) => {
        const [adultos, menores, edades] = queryParams[key].split("|");
        return {
          num_adultos: parseInt(adultos, 10),
          num_menores: parseInt(menores, 10),
          edad_menores: edades ? edades.replace(/,/g, "|") : "", // Convertimos comas en pipes
        };
      });

    const totalPersonas = habitaciones.reduce(
      (total, habitacion) =>
        total + habitacion.num_adultos + habitacion.num_menores,
      0
    );

    const habitacionesFormatted = Object.keys(queryParams)
      .filter((key) => key.startsWith("pax")) // Filtramos las claves que empiezan con 'pax'
      .map((key, index) => {
        const [adultos, menores, edades] = queryParams[key].split("|");

        // Convertir edades (si las hay) en un array
        const edadesArray =
          edades === "0" || !edades ? [] : edades.split(",").map(Number);

        // Devolver el objeto con la estructura requerida
        return {
          room: index + 1, // El número de la habitación es el índice + 1
          adults: parseInt(adultos, 10),
          minors: parseInt(menores, 10),
          ages: edadesArray,
        };
      });

    setRooms(habitacionesFormatted);
    setTotalRooms(habitaciones);
    setTotalPax(totalPersonas);

    // Construir el objeto final
    const request = {
      fechas: {
        checkin,
        checkout,
      },
      alojamiento: {
        id,
        tipo,
      },
      habitaciones,
    };

    setParamsUrl(request);

    return request;
  };

  useEffect(() => {
    requestData = construirRequest(); // Esto solo se ejecuta cuando `location.search` cambia
  }, [location.search]);

  useEffect(() => {
    // Obtener la URL actual
    const queryParams = parseQueryParams(location.search);

    // Obtener las fechas de entrada y salida de la URL
    const checkInDate = queryParams.in;
    const checkOutDate = queryParams.out;

    if (checkInDate && checkOutDate) {
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);
      const timeDifference = checkOut - checkIn;
      const nights = timeDifference / (1000 * 3600 * 24);

      setTotalNights(nights);
    }
  }, []);

  useEffect(() => {
    const fetchMarkupPanel = async () => {
      try {
        const markupData = await fetchMarkup();
        setMarkup(markupData.markup);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchMarkupPanel();
  }, []);

  // Llamada combinada para hoteles y tarifas
  useEffect(() => {
    let isMounted = true; // Control de limpieza

    const fetchData = async () => {
      setLoading(true);

      try {
        // Paso 1: Cargar y mostrar `hotelsData`
        const hotelsData = await fetchHotels();

        if (isMounted) {
          setFilteredHotels(hotelsData); // Mostrar datos iniciales
          setLoading(false);
        }

        const [ratesData, filtersData] = await Promise.all([
          fetchRates(),
          fetchFilter(),
        ]);

        if (isMounted) {
          if (ratesData.length > 0) {
            setNameDestination(ratesData[0].destino);
          }

          const combinedHotels = hotelsData
            .filter((hotel) =>
              ratesData.some((avail) => avail.code === hotel.id_bol)
            )
            .map((hotel) => {
              const availability = ratesData.find(
                (avail) => avail.code === hotel.id_bol
              );
              return { ...hotel, ...availability };
            });

          const filter = combinedHotels.map((hotel) => {
            return hotel.zona;
          });
          const filterBoardName = ratesData?.map((hab) => {
            return hab?.habitaciones?.[0]?.rates?.[0]?.boardName;
          });
          const filterBoardCode = ratesData?.map((hab) => {
            return hab?.habitaciones?.[0]?.rates?.[0]?.boardCode;
          });

          const zonasUnicas = [...new Set(filter)];
          const boardNameUnicas = [...new Set(filterBoardName)];
          const boardCodeUnicas = [...new Set(filterBoardCode)];
          const boardData = boardNameUnicas.map((name, index) => ({
            id: boardCodeUnicas[index],
            descripcion: name,
          }));

          setFilteredHotels(combinedHotels); // Actualizar con datos combinados
          setHotels(combinedHotels);
          setFilters([
            { nombre: "Plan de alimentación", lista_filters: boardData, id: 6 },
            ...filtersData,
            { nombre: "Ubicación", lista_filters: zonasUnicas, id: 5 },
          ]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (isMounted) console.log("Failed to load data. Please try again.");
      }
    };

    fetchData();

    return () => {
      isMounted = false; // Limpiar al desmontar
    };
  }, [location.search]);

  // Función para cargar los hoteles
  const fetchHotels = async () => {
    const objAlojamiento = { alojamiento: requestData.alojamiento };

    try {
      const response = await getHotels(objAlojamiento);
      return response;
    } catch (error) {
      console.error("Error fetching hotels:", error);
      return [];
    }
  };

  // Función para cargar tarifas
  const fetchRates = async () => {
    try {
      const response = await getHotelAvailability(requestData);
      return response;
    } catch (error) {
      console.error("Error fetching rates:", error);
      return [];
    }
  };
  // Función para cargar filtros de busqueda
  const fetchFilter = async () => {
    try {
      const response = await getHotelFilters(queryParams.id);
      return response;
    } catch (error) {
      console.error("Error fetching rates:", error);
      return [];
    }
  };

  /**
   * Incrementa el número de resultados visibles en 10.
   * @returns {void}
   */
  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 10);
  };

  /**
   * Calcula el precio total y el precio por noche en función de la tarifa de venta y el margen de beneficio.
   *
   * @param {number} sellingRate - The initial selling rate of the hotel.
   * @param {number} markup - The markup percentage to be added to the selling rate.
   * @returns {Array} An array containing the price per night and the total rate.
   */

  useEffect(() => {
    // Filtrar hoteles cuando selectedValues cambian
    filterHotels();
  }, [selectedValues, hotels]);

  // Función para manejar checkboxes
  const handleCheckboxChange = (sectionId, filterId, checked) => {
    setSelectedValues((prev) => {
      const section = prev[sectionId] || [];
      return {
        ...prev,
        [sectionId]: checked
          ? [...section, filterId]
          : section.filter((id) => id !== filterId),
      };
    });
  };

  // Función para manejar radios
  const handleRadioChange = (sectionId, filterId) => {
    setSelectedValues((prev) => ({ ...prev, [sectionId]: filterId }));
  };
  // Función para filtrar hoteles
  const filterHotels = () => {
    const hasActiveFilters = Object.values(selectedValues).some((value) => {
      if (value === null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });

    if (!hasActiveFilters) {
      setFilteredHotels(hotels);
      return;
    }

    const filtered = hotels.filter((hotel) => {
      let matches = true;

      // Filtrar por Categoría (radio)
      if (selectedValues[1] && hotel.categoriaId !== selectedValues[1])
        return false;

      // Filtrar por Plan de Alimentación (checkbox)
      if (selectedValues[2] && selectedValues[2].length > 0) {
        matches = matches && selectedValues[2].includes(hotel.planes[0]);
      }

      // Filtrar por Tipo de Hospedaje (radio)
      if (selectedValues[3]) {
        matches = matches && hotel.tipo_hospedaje === selectedValues[3];
      }

      // Filtrar por Segmentos (checkbox)
      if (selectedValues[4] && selectedValues[4].length > 0) {
        const segmentosSeleccionados = selectedValues[4].map(Number); // Convertir a números
        matches =
          matches &&
          segmentosSeleccionados.some((segmento) =>
            hotel.segmentos.includes(segmento)
          );
      }
      // Filtrar por Plan de Alimentación (checkbox)
      if (selectedValues[5]) {
        matches = matches && hotel.zona === selectedValues[5];
      }

      if (selectedValues[6] && selectedValues[6].length > 0) {
        matches =
          matches &&
          selectedValues[6].includes(hotel.habitaciones[0].rates[0].boardCode);
      }
      return matches;
    });
    setFilteredHotels(filtered);
  };

  /**
   * Esta función busca un valor numérico al comienzo
   * de la cadena `categoriaId` proporcionada, que representa la
   * calificación de estrellas de una categoría de hotel. Si se encuentra un número, se
   * analiza y se devuelve como un entero. Si no se encuentra ningún número,
   * la función devuelve 0.
   *
   * @param {string} categoriaId - La cadena de identificación de la categoría, que se espera
   * comience con un valor numérico que indique el recuento de estrellas.
   * @returns {number} - El recuento de estrellas extraído o 0 si no se encuentra.
   */

  const getStarCount = (categoriaId) => {
    const starMatch = categoriaId.match(/\d+/); // Encuentra el primer número en la cadena
    return starMatch ? parseInt(starMatch[0], 10) : 0; // Devuelve el número como entero o 0 si no encuentra
  };

  const HotelStars = ({ categoriaId }) => {
    const starCount = getStarCount(categoriaId); // Obtener el número de estrellas

    // Crear un arreglo de estrellas para renderizar
    const stars = Array.from({ length: starCount }, (_, index) => (
      <IoStarSharp key={index} className="star-icon" />
    ));

    return <div className="section-stars">{stars}</div>;
  };

  const handleSelectHotel = (hotel) => {
    const checkIn = paramsUrl.fechas.checkin;
    const checkOut = paramsUrl.fechas.checkout;
    const storedArray = rooms;

    const paxParams = storedArray
      .map((hab, index) => {
        const adultos = hab.adults;
        const menores = hab.minors;
        const edades = hab.ages.length > 0 ? hab.ages.join(",") : "0";
        return `pax${index + 1}=${adultos}|${menores}|${edades}`;
      })
      .join("&"); // Unir todos los parámetros de habitaciones con "&"
    let url = `/hotel?idHotel=${hotel.id_atv3}&in=${checkIn}&out=${checkOut}&hab=${storedArray.length}&${paxParams}`;

    // Usar React Router para redirigir
    navigate(url);
  };

  return (
    <div className="container pb-5 pt-5">
      {loading && <Loader />}
      <Row>
        <Col lg={3} className="filters">
          <SearchEngine
            isModify={true}
            totalRooms={totalRooms}
            destination={nameDestination}
            idLocation={queryParams.id}
            arrayRooms={rooms}
            selectedDates={selectedDates}
          />
          <div className="filters pt-5 pb-5">
            <h4>Filtros</h4>
            <Card className="card-fliters shadow-box text-start">
              <Card.Body>
                {filters && (
                  <Accordion defaultActiveKey="0">
                    {filters?.map((section, index) => (
                      <Accordion.Item
                        eventKey={index.toString()}
                        key={section?.id}
                      >
                        <Accordion.Header>{section?.nombre}</Accordion.Header>
                        <Accordion.Body>
                          <Form>
                            {/* Opción "Todos" para secciones con Radio Buttons */}
                            {[
                              "Categoría",
                              "Tipo de Hospedaje",
                              "Ubicación",
                            ].includes(section?.nombre) && (
                              <Form.Check
                                type="radio"
                                name={`radio-group-${section?.id}`}
                                label="Todos"
                                id={`${section?.id}-all`}
                                checked={!selectedValues[section?.id]}
                                onChange={() =>
                                  handleRadioChange(section?.id, null)
                                }
                                style={{ fontSize: "14px" }}
                              />
                            )}

                            {section?.lista_filtros?.map((filtro) => (
                              <Form.Group key={filtro?.id} className="mb-2">
                                {["Segmento"].includes(section?.nombre) ? (
                                  <Form.Check
                                    type="checkbox"
                                    label={filtro?.descripcion}
                                    id={`${section?.id}-${filtro?.id}`}
                                    checked={
                                      selectedValues[section?.id]?.includes(
                                        filtro?.id
                                      ) || false
                                    }
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        section?.id,
                                        filtro?.id,
                                        e.target.checked
                                      )
                                    }
                                    style={{ fontSize: "14px" }}
                                  />
                                ) : (
                                  <Form.Check
                                    type="radio"
                                    name={`radio-group-${section?.id}`}
                                    label={filtro?.descripcion}
                                    id={`${section?.id}-${filtro?.id}`}
                                    checked={
                                      selectedValues[section?.id] === filtro?.id
                                    }
                                    onChange={() =>
                                      handleRadioChange(section?.id, filtro?.id)
                                    }
                                    style={{ fontSize: "14px" }}
                                  />
                                )}
                              </Form.Group>
                            ))}
                            {section?.lista_filters
                              ?.slice(0, 5)
                              ?.map((filtro) => (
                                <Form.Group key={filtro?.id} className="mb-2">
                                  {["Plan de alimentación"].includes(
                                    section?.nombre
                                  ) ? (
                                    <Form.Check
                                      type="checkbox"
                                      label={filtro?.descripcion}
                                      id={`${section?.id}-${filtro?.id}`}
                                      checked={
                                        selectedValues[section?.id]?.includes(
                                          filtro?.id
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          section?.id,
                                          filtro?.id,
                                          e.target.checked
                                        )
                                      }
                                      style={{ fontSize: "14px" }}
                                    />
                                  ) : (
                                    <Form.Check
                                      type="radio"
                                      name={`radio-group-${section?.id}`}
                                      label={filtro}
                                      id={`${section?.id}-${filtro}`}
                                      // label={"Todos"}
                                      checked={
                                        selectedValues[section?.id] === filtro
                                      }
                                      onChange={() =>
                                        handleRadioChange(section?.id, filtro)
                                      }
                                      style={{ fontSize: "14px" }}
                                    />
                                  )}
                                </Form.Group>
                              ))}
                          </Form>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </Card.Body>
            </Card>
          </div>
        </Col>
        <Col lg={8} className="hotel-list">
          {!loading && (
            <Row>
              {filteredHotels.length === 0 && (
                <div className="text-center mt-5">
                  <h1>0 hoteles disponibles con sus filtros de busqueda</h1>
                  <br />
                  <h2 className="text-muted">
                    No hay hoteles disponibles con estos filtros
                  </h2>
                </div>
              )}
              {filteredHotels?.slice(0, visibleCount)?.map((hotel, index) => (
                <Col
                  lg={12}
                  md={6}
                  sm={12}
                  xs={12}
                  className={`pt-5`}
                  key={index + 1}
                >
                  <Card
                    className={`rounded-4 ${
                      hotel.tratoExclusivo === 1 && "preferencial-rate"
                    } text-start shadow-box`}
                    style={
                      hotel?.tratoExclusivo === 1
                        ? { minHeight: "305px" }
                        : { minHeight: "240px" }
                    }
                  >
                    {hotel?.tratoExclusivo === 1 ? (
                      <div className="category preferential">
                        <span>Hotel tarifa preferencial</span>
                      </div>
                    ) : hotel?.tratoExclusivo === 2 ? (
                      <div className="category">
                        <a>
                          <img
                            src={star}
                            alt="icon-star"
                            style={{
                              width: "30px",
                              position: "absolute",
                              left: "0px",
                              borderTopLeftRadius: "1rem",
                            }}
                          />
                        </a>
                      </div>
                    ) : (
                      <></>
                    )}

                    <Row>
                      <Col lg={4} md={12} sm={12} className="hotel_img">
                        <div
                          onClick={() => handleSelectHotel(hotel)}
                          style={{ cursor: "pointer" }}
                        >
                          {" "}
                          <img
                            src={`http://photos.hotelbeds.com/giata/bigger/${hotel?.imagenes[0]?.url}`}
                            className="img-hotel-search"
                            style={
                              hotel.tratoExclusivo === 1
                                ? {
                                    height: "270px",
                                    borderTopLeftRadius: "1rem",
                                    borderBottomLeftRadius: "1rem",
                                  }
                                : {
                                    height: "240px",
                                    borderTopLeftRadius: "1rem",
                                    borderBottomLeftRadius: "1rem",
                                  }
                            }
                            alt="image-destinations"
                          />
                        </div>
                      </Col>
                      <Col
                        lg={4}
                        md={12}
                        sm={12}
                        className="hotel-price ps-lg-0 ps-4"
                      >
                        <div className="detail-hotel pt-3">
                          <span className="name-hotel">{hotel?.nombre}</span>
                          <br />
                          <span className="location-hotel">{hotel?.zona}</span>
                        </div>
                        {hotel?.categoria ? (
                          <div className="section-stars">
                            {hotel?.categoriaId === "BOU" ? (
                              <h4>{hotel?.categoria}</h4>
                            ) : (
                              <HotelStars categoriaId={hotel?.categoriaId} />
                            )}
                          </div>
                        ) : (
                          <Skeleton count={3} width={200} />
                        )}

                        <div className="section-nights pt-4 pb-4">
                          <span className="info-nights">
                            {totalNights} noches, {totalPax}{" "}
                            {totalPax > 1 ? "personas" : "persona"}
                          </span>
                        </div>

                        <div className="section-bage">
                          <Row>
                            <Col className="col-lg-12 col-sm-6">
                              {/**Se oculta en lo que se define de donde viene la información */}
                              {/* <div className='pb-3'>
                                                      <Badge bg="danger" className='badge-discount'>40% off</Badge>
                                                  </div> */}
                            </Col>
                            <Col className="col-lg-12 col-sm-6">
                              <div>
                                <Button className="styled-btn payment-methods">
                                  Métodos de pago
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      {hotel?.habitaciones?.[0]?.rates?.[0] ? (
                        <Col lg={4} md={12} sm={12} className="hotel-rate">
                          <div
                            className="rates pt-4 text-end"
                            style={{ paddingRight: "20px" }}
                          >
                            <span
                              className="name-hotel"
                              style={{ fontSize: "17px" }}
                            >
                              Precio por noche:{" "}
                              {Currency(
                                totalPrices(
                                  hotel?.habitaciones?.[0]?.rates?.[0]?.net,
                                  totalNights,
                                  markup
                                )[0]
                              ) + "MXN"}
                            </span>
                            <br />
                            <span
                              className="text-danger"
                              style={{ fontSize: "17px" }}
                            >
                              impuestos incluidos
                            </span>
                            <p>
                              <span className="location-hotel">
                                Precio total{" "}
                              </span>
                              <br />
                              <span
                                className="fw-bold"
                                style={{ fontSize: "20px" }}
                              >
                                {Currency(
                                  totalPrices(
                                    hotel.habitaciones[0].rates[0].net,
                                    totalNights,
                                    markup
                                  )[1]
                                ) + "MXN"}
                              </span>
                            </p>
                            <div className="section-meal">
                              <span className="meal">Plan de alimentos:</span>
                              <div>
                                <Col style={{ paddingTop: "0px" }}>
                                  <span
                                    className="fw-bold"
                                    style={{ fontSize: "15px" }}
                                  >
                                    {hotel.habitaciones[0].rates[0].boardName}
                                  </span>
                                </Col>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end mb-2">
                              <button
                                className="btn-blue mt-3 text-white"
                                style={{ height: "35px", fontSize: "14px" }}
                                onClick={() => handleSelectHotel(hotel)}
                              >
                                Seleccionar Hotel
                              </button>
                            </div>
                          </div>
                        </Col>
                      ) : (
                        <Col lg={4} md={12} sm={12} className="hotel-rate">
                          <div style={{ paddingTop: "35px" }}>
                            <Skeleton count={8} width={250} height={20} />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Card>
                </Col>
              ))}
              {visibleCount < filteredHotels.length && (
                <div className="d-flex justify-content-center">
                  <Col md={2} className="pt-5">
                    <Row>
                      <Button
                        className="btn-blue"
                        style={{ height: "35px", fontSize: "16px" }}
                        onClick={handleShowMore}
                      >
                        Ver más
                      </Button>
                    </Row>
                  </Col>
                </div>
              )}
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};
